import Phaser from 'phaser';
import { RunManager } from '../systems/RunManager';
import { EVENTS, GameEvent, EventChoice } from '../data/events';
import { SfxManager } from '../utils/SfxManager';
import { RESOURCE_INFO } from '../systems/ResourceManager';
import { ResourceType } from '../systems/ResourceManager';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';

// === SCÈNE D'ÉVÉNEMENT ===
// Affiche un événement narratif avec 2-3 choix.

export class EventScene extends Phaser.Scene {
  private run!: RunManager;
  private event!: GameEvent;
  private sfx = new SfxManager();
  private hasChosen = false;

  constructor() {
    super({ key: 'EventScene' });
  }

  init(data: { run?: RunManager }): void {
    this.run = data?.run ?? new RunManager();
    this.hasChosen = false;
    // Choisir un event aléatoire
    this.event = EVENTS[Math.floor(Math.random() * EVENTS.length)];
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    // Fond
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e);

    // Titre
    this.add.text(GAME_CX, 50, `${this.event.emoji} ${this.event.title}`, {
      fontSize: '22px', color: '#3498db', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);

    // Description
    this.add.text(GAME_CX, 130, this.event.description, {
      fontSize: '14px', color: '#ecf0f1', fontFamily: FONT_BODY,
      wordWrap: { width: Math.min(550, GAME_W - 40) }, align: 'center', lineSpacing: 6,
    }).setOrigin(0.5);

    // Choix
    const startY = 230;
    const spacing = 70;

    for (let i = 0; i < this.event.choices.length; i++) {
      const choice = this.event.choices[i];
      this.drawChoice(choice, startY + i * spacing, i);
    }

    // Affichage or et HP du joueur
    const hp = this.run.getPlayerHp();
    const gold = this.run.getGold();
    this.add.text(GAME_CX, GAME_H - 80, `❤️ ${hp > 0 ? hp : '?'} HP    💰 ${gold} Or`, {
      fontSize: '14px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5);
  }

  private drawChoice(choice: EventChoice, y: number, index: number): void {
    // Vérifier si le joueur peut payer le coût en or
    const goldCost = choice.effect.gold && choice.effect.gold < 0 ? -choice.effect.gold : 0;
    const canAfford = this.run.getGold() >= goldCost;

    // Description de l'effet
    const effectParts: string[] = [];
    if (choice.effect.hp && choice.effect.hp > 0) effectParts.push(`+${choice.effect.hp} HP`);
    if (choice.effect.hp && choice.effect.hp < 0) effectParts.push(`${choice.effect.hp} HP`);
    if (choice.effect.gold && choice.effect.gold > 0) effectParts.push(`+${choice.effect.gold} or`);
    if (choice.effect.gold && choice.effect.gold < 0) effectParts.push(`${choice.effect.gold} or`);
    if (choice.effect.resources) {
      for (const [type, amount] of Object.entries(choice.effect.resources)) {
        if (amount && amount > 0) {
          const info = RESOURCE_INFO[type as ResourceType];
          effectParts.push(`${info.emoji} +${amount} ${info.name}`);
        }
      }
    }
    const effectStr = effectParts.length > 0 ? ` (${effectParts.join(', ')})` : '';

    const color = canAfford ? '#ecf0f1' : '#7f8c8d';
    const btn = this.add.text(GAME_CX, y, `${choice.emoji} ${choice.label}${effectStr}`, {
      fontSize: '15px', color, fontFamily: FONT_BODY,
      backgroundColor: '#1e2d4a', padding: { x: 16, y: 10 },
      wordWrap: { width: Math.min(500, GAME_W - 40) },
    }).setOrigin(0.5);

    if (!canAfford) return;

    btn.setInteractive({ useHandCursor: true });

    btn.on('pointerover', () => {
      if (!this.hasChosen) btn.setColor('#f1c40f');
    });
    btn.on('pointerout', () => {
      if (!this.hasChosen) btn.setColor('#ecf0f1');
    });
    btn.on('pointerdown', () => {
      if (this.hasChosen) return;
      this.hasChosen = true;
      this.sfx.select();
      this.applyChoice(choice, btn);
    });
  }

  private applyChoice(choice: EventChoice, btn: Phaser.GameObjects.Text): void {
    const effect = choice.effect;

    // Cas spécial : le fantôme joueur (50/50)
    let actualGold = effect.gold ?? 0;
    if (this.event.id === 'gambling_ghost' && choice.label.includes('50/50')) {
      actualGold = Math.random() < 0.5 ? 20 : -15;
    }

    // Appliquer l'or
    if (actualGold !== 0) {
      this.run.addGold(actualGold);
    }

    // Appliquer les HP à toute l'équipe vivante (soin OU dégâts)
    // Les événements ne tuent jamais : applyHpDeltaToAliveTeam clampe à 1 min.
    if (effect.hp) {
      this.run.applyHpDeltaToAliveTeam(effect.hp);
    }

    // Appliquer les ressources
    if (effect.resources) {
      this.run.getCamp().getResources().addMultiple(effect.resources);
    }

    // Feedback visuel — suffixe "(équipe)" si l'équipe a > 1 membre vivant
    const resultParts: string[] = [];
    const aliveCount = this.run.getAliveTeam().length;
    const teamSuffix = aliveCount > 1 ? ' (équipe)' : '';
    if (effect.hp && effect.hp > 0) resultParts.push(`❤️ +${effect.hp} HP${teamSuffix}`);
    if (effect.hp && effect.hp < 0) resultParts.push(`💔 ${effect.hp} HP${teamSuffix}`);
    if (actualGold > 0) resultParts.push(`💰 +${actualGold} Or`);
    if (actualGold < 0) resultParts.push(`💸 ${actualGold} Or`);
    if (effect.resources) {
      for (const [type, amount] of Object.entries(effect.resources)) {
        if (amount && amount > 0) {
          const info = RESOURCE_INFO[type as ResourceType];
          resultParts.push(`${info.emoji} +${amount} ${info.name}`);
        }
      }
    }
    if (resultParts.length === 0) resultParts.push('Rien ne se passe...');

    btn.setColor('#f1c40f');

    const resultText = this.add.text(GAME_CX, GAME_H - 160, resultParts.join('  '), {
      fontSize: '18px', color: '#2ecc71', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setAlpha(0);

    this.tweens.add({
      targets: resultText,
      alpha: 1,
      duration: 400,
    });

    // Tracking + compléter le nœud et retour carte
    this.run.getMeta().addStat('eventsCompleted');
    this.run.completeCurrentNode();

    this.time.delayedCall(1500, () => {
      startSceneWithFade(this, 'MapScene', { run: this.run });
    });
  }
}
